import WorksheetReportNotice from "modules/worksheet/WorksheetReportNotice";
import {
  Box,
  Card,
  Checkbox,
  Divider,
  Grid,
  Icon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Controller, FormProvider, useForm } from "react-hook-form";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import MDDatePicker from "components/MDDatePicker";
import { GenerateReportMutationVariables, ReportType } from "generated/graphql";
import { capitalCase } from "change-case";
import { useEffect, useMemo, useRef, useState } from "react";
import { kebabCase } from "lodash";
import useGenerateReport from "hooks/organization/reports/useGenerateReport";
import DataTable from "components/Tables/DataTable/DataTable";
import useReportTableProposalDetails from "hooks/organization/reports/types/useReportTableProposalDetails";
import ScrollAsyncAutocomplete from "components/ScrollAsyncAutocomplete/ScrollAsyncAutocomplete";
import CompanyOptions from "modules/companies/CompanyOptions";
import { useGetCompanyLazy } from "hooks/companies/useGetCompanyLazy";
import { zodResolver } from "@hookform/resolvers/zod";
import { createProposalSchema } from "DDD/action-objects/ProposalCreate";
import OrganizationUsersOptions from "modules/organization/OrganizationUsersOptions/OrganizationUsersOptions";
import JobOptions from "modules/jobs/JobOptions";
import useGetOrganizationUser from "hooks/users/useGetOrganizationUser";
import { ProductType, ProposalStatus } from "generated/graphql";
import { enumToValueOptions } from "utils/enums/enumToValueOptions";
import { z } from "zod";
import dayjs from "dayjs";

// Extended schema to include estimatorId, jobId, type (ProductType), and status (ProposalStatus)
const reportFilterSchema = createProposalSchema.extend({
  estimatorId: z.string().optional(),
  jobId: z.string().optional(),
  type: z.string().optional(),
  status: z.string().optional(),
  wonAt: z.boolean().optional(),
});

type ReportFilterFormData = z.infer<typeof reportFilterSchema>;

const getReportDefaultValues = (
  initialValues: Partial<ReportFilterFormData>
): Partial<ReportFilterFormData> => ({
  companyId: "",
  contactId: "",
  source: undefined,
  estimatorId: "",
  jobId: "",
  type: "",
  status: "",
  wonAt: false,
  ...initialValues,
});

const ReportProposalDetails = () => {
  const {
    handleSubmit,
    formState: { errors },
  } = useForm();

  let today = dayjs().format("YYYY-MM-DD 00:00:00");

  // Set start date to 1 month ago
  const startDateFormatted = dayjs().subtract(1, "month").format("YYYY-MM-DD 00:00:00");

  const [date, setDate] = useState({ startDate: startDateFormatted, endDate: today });
  const [generateReport, { data: generateReportData }] = useGenerateReport();
  const [companyName, setCompanyName] = useState("");
  const [estimatorId, setEstimatorId] = useState("");
  const [estimatorName, setEstimatorName] = useState("");
  const [jobId, setJobId] = useState("");
  const [jobName, setJobName] = useState("");
  const [productType, setProductType] = useState("");
  const [productTypeName, setProductTypeName] = useState("");
  const [status, setStatus] = useState("");
  const [statusName, setStatusName] = useState("");
  const [wonAt, setWonAt] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Initialize column visibility state
  const [columnVisibility, setColumnVisibility] = useState({
    external_id_with_prefix: true,
    company_id: true,
    company_name: true,
    estimator_name: true,
    job_name: true,
    stage_name: true,
    address_line_1: true,
    city: true,
    state: true,
    zip: true,
    material_type: true,
    material_name: true,
    job_size: true,
    material_measurement_material: true,
    material_material_needed_override: true,
    status: true,
    created_at: true,
    profit: true,
    net_margin_percentage: true,
    overall_total: true,
    created_by: true,
    stage_id: false,
    source: false,
    material_cost: false,
    equipment_cost: false,
    labour_cost: false,
    additional_costs: false,
    direct_costs: false,
    overhead_cost: false,
    indirect_costs: false,
    total_cost: false,
    profit_percentage: false,
    gross_margin: false,
    gross_margin_percentage: false,
    operating_margin: false,
    operating_margin_percentage: false,
    net_margin: false,
    signed_at: false,
    signee_initials: false,
    signee_name: false,
    viewed_at: false,
    proposal_notes: false,
  });

  const columns = useMemo(() => {
    return [
      {
        Header: "Proposal Id",
        accessor: "external_id_with_prefix",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
        visible: columnVisibility.external_id_with_prefix,
      },
      {
        Header: "Company",
        accessor: "company_id",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
        visible: columnVisibility.company_id,
      },
      {
        Header: "Company Name",
        accessor: "company_name",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
        visible: columnVisibility.company_name,
      },
      {
        Header: "Estimator Name",
        accessor: "estimator_name",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
        visible: columnVisibility.estimator_name,
      },
      {
        Header: "Job Name",
        accessor: "job_name",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
        visible: columnVisibility.job_name,
      },
      {
        Header: "Stage Name",
        accessor: "stage_name",
        Cell: ({ value }) => capitalCase(value ?? ""),
        disableSortBy: true,
        visible: columnVisibility.stage_name,
      },
      {
        Header: "Stage Address",
        accessor: "address_line_1",
        Cell: ({ value }) => value ?? "",
        filter: "includes",
        disableSortBy: true,
        visible: columnVisibility.address_line_1,
      },
      {
        Header: "Stage city",
        accessor: "city",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.city,
      },
      {
        Header: "Stage State",
        accessor: "state",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.state,
      },
      {
        Header: "Stage Zip",
        accessor: "zip",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.zip,
      },
      {
        Header: "Product Type",
        accessor: "material_type",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.material_type,
      },
      {
        Header: "Product Name",
        accessor: "material_name",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.material_name,
      },
      {
        Header: "Product Job Size",
        accessor: "job_size",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.job_size,
      },
      {
        Header: "Product Material Type",
        accessor: "material_measurement_material",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.material_measurement_material,
      },
      {
        Header: "Product Material Needed",
        accessor: "material_material_needed_override",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.material_material_needed_override,
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.status,
      },
      {
        Header: "Created At",
        accessor: "created_at",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.created_at,
      },
      {
        Header: "Profit",
        accessor: "profit",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.profit,
      },
      {
        Header: "Net Margin Percentage",
        accessor: "net_margin_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.net_margin_percentage,
      },
      {
        Header: "Overall Total",
        accessor: "overall_total",
        Cell: ({ value }) => formatCurrency(value) ?? "",
        disableSortBy: true,
        visible: columnVisibility.overall_total,
      },
      {
        Header: "Estimator",
        accessor: "created_by",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.created_by,
      },
      // Hidden columns
      {
        Header: "Stage Id",
        accessor: "stage_id",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.stage_id,
      },
      {
        Header: "Source",
        accessor: "source",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.source,
      },
      {
        Header: "Material Cost",
        accessor: "material_cost",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.material_cost,
      },
      {
        Header: "Equipment Cost",
        accessor: "equipment_cost",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.equipment_cost,
      },
      {
        Header: "Labor Cost",
        accessor: "labour_cost",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.labour_cost,
      },
      {
        Header: "Additional Costs",
        accessor: "additional_costs",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.additional_costs,
      },
      {
        Header: "Direct Costs",
        accessor: "direct_costs",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.direct_costs,
      },
      {
        Header: "Overhead Cost",
        accessor: "overhead_cost",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.overhead_cost,
      },
      {
        Header: "Indirect Costs",
        accessor: "indirect_costs",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.indirect_costs,
      },
      {
        Header: "Total Cost",
        accessor: "total_cost",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.total_cost,
      },
      {
        Header: "Profit Percentage",
        accessor: "profit_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.profit_percentage,
      },
      {
        Header: "Gross Margin",
        accessor: "gross_margin",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.gross_margin,
      },
      {
        Header: "Gross Margin Percentage",
        accessor: "gross_margin_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.gross_margin_percentage,
      },
      {
        Header: "Operating Margin",
        accessor: "operating_margin",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.operating_margin,
      },
      {
        Header: "Operating Margin Percentage",
        accessor: "operating_margin_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.operating_margin_percentage,
      },
      {
        Header: "Net Margin",
        accessor: "net_margin",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.net_margin,
      },
      {
        Header: "Signed At",
        accessor: "signed_at",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.signed_at,
      },
      {
        Header: "Signee Initials",
        accessor: "signee_initials",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.signee_initials,
      },
      {
        Header: "Signee Name",
        accessor: "signee_name",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.signee_name,
      },
      {
        Header: "Viewed At",
        accessor: "viewed_at",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.viewed_at,
      },
      {
        Header: "Proposal Notes",
        accessor: "proposal_notes",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposal_notes,
      },
    ];
  }, [columnVisibility]);

  const companyInitialId = null;

  const [requestData, setRequestData] = useState<GenerateReportMutationVariables | null>(null);
  const [triggerRefetch, setTriggerRefetch] = useState(false);
  const [getCompany, { company }] = useGetCompanyLazy();

  const methods = useForm<ReportFilterFormData>({
    resolver: zodResolver(reportFilterSchema),
    defaultValues: getReportDefaultValues({ companyId: companyInitialId }),
  });

  const companyId = methods.watch("companyId");

  const {
    tableData: originalTableData,
    getReportProposalDetails,
    pagination,
    metadata,
  } = useReportTableProposalDetails(
    date.startDate,
    date.endDate,
    companyId,
    estimatorId,
    jobId,
    productType,
    status,
    wonAt,
    columns
  );

  const downloadFileRef = useRef(null);

  useEffect(() => {
    if (generateReportData?.generateReport) {
      downloadFileRef.current.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${encodeURI(
        generateReportData.generateReport
      )}`;
      downloadFileRef.current.download = `bitumio-${kebabCase(requestData.type)}-report.xlsx`;
      downloadFileRef.current.click();
    }
  }, [generateReportData]);

  const changeDate = async (selectedDate: Array<Date>, type) => {
    const formattedDate = dayjs(selectedDate[0]).format("YYYY-MM-DD 00:00:00");
    setDate((date) => ({ ...date, [type]: formattedDate }));
  };

  const onSubmit = (values) => {
    const variables = {
      ...values,
      ...date,
      companyId,
      estimatorId,
      jobId,
      productTypeId: productType,
      status,
      wonAt,
      page: 1,
      first: 25,
    };
    setRequestData(variables);
    getReportProposalDetails({ variables });
  };

  const handleDownload = (e) => {
    const variables = {
      type: ReportType.PROPOSAL_DETAILS,
      ...date,
      companyId,
      estimatorId,
      jobId,
      productTypeId: productType,
      status,
      wonAt,
    };
    setRequestData(variables);
    generateReport({ variables });
  };

  // Format dates for display
  const formatDisplayDate = (dateString: string) => {
    return dayjs(dateString).format("MMM D, YYYY");
  };

  // Format currency for display
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const handleColumnVisibilityChange = (accessor) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [accessor]: !prev[accessor],
    }));
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (companyId) {
      getCompany({ id: companyId });
    }
  }, [getCompany, companyId]);

  // Sync companyName with selected company
  useEffect(() => {
    if (company?.name) {
      setCompanyName(company.name);
    } else if (companyId === null || companyId === undefined) {
      setCompanyName("");
    }
  }, [company, companyId]);

  // Sync estimator name with selected estimator
  const [getEstimator, { user: estimator }] = useGetOrganizationUser();

  useEffect(() => {
    if (estimatorId) {
      getEstimator({ id: estimatorId });
    }
  }, [getEstimator, estimatorId]);

  useEffect(() => {
    if (estimatorId === null || estimatorId === undefined || estimatorId === "") {
      setEstimatorName("");
    } else if (estimator?.firstName) {
      setEstimatorName(`${estimator.firstName} ${estimator.lastName}`);
    }
  }, [estimator, estimatorId]);

  // Create ProductType options for dropdown
  const productTypeOptions = enumToValueOptions(ProductType);

  // Create ProposalStatus options for dropdown
  const statusOptions = enumToValueOptions(ProposalStatus);

  const tableData = {
    ...originalTableData,
    rows: [
      ...(originalTableData?.rows || []),
      // Add grand total row if we're on the last page and have metadata
      ...(pagination?.hasNextPage === false && metadata?.totals?.overall_total
        ? [
            {
              external_id_with_prefix: "Total",
              overall_total: metadata.totals.overall_total,
              style: {
                fontWeight: "bold",
                backgroundColor: "#f5f5f5",
                borderTop: "2px solid #ddd",
              },
            },
          ]
        : []),
    ],
  };

  return (
    <Box
      sx={{
        "& .css-1h9h5al-MuiAutocomplete-root .MuiOutlinedInput-root": {
          padding: "5px !important",
        },
      }}
    >
      <DashboardPage>
        <WorksheetReportNotice missingCosts={metadata?.totals?.items_without_costs ?? 0} />
        <FormProvider {...methods}>
          <MDBox mb={6} component="form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3} alignItems="center" display="flex" justifyContent="center">
              <Grid item xs={12} sx={{ mb: 2 }}>
                <Card>
                  <MDBox
                    p={3}
                    lineHeight={1}
                    display="flex"
                    alignItems="center"
                    width="100%"
                    justifyContent="space-between"
                  >
                    <Box>
                      <MDTypography variant="h4" fontWeight="medium">
                        {capitalCase(ReportType.PROPOSAL_DETAILS)} Report
                      </MDTypography>
                      <MDTypography variant="body2" color="text" mt={1}>
                        Track proposal status and conversion metrics
                      </MDTypography>
                    </Box>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: "info.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon sx={{ color: "#fff" }}>description</Icon>
                    </Box>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <MDBox p={3}>
                    <MDTypography variant="h6" mb={2}>
                      Filter Options
                    </MDTypography>
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={6} md={1.5} lg={1.5}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDTypography variant="body2" fontWeight="regular" mb={1}>
                            Start Date
                          </MDTypography>
                          <MDDatePicker
                            defaultValue={startDateFormatted}
                            onChange={(val) => changeDate(val, "startDate")}
                            fullWidth
                          />
                        </MDBox>
                      </Grid>
                      <Grid item xs={6} md={1.5} lg={1.5}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDTypography variant="body2" fontWeight="regular" mb={1}>
                            End Date
                          </MDTypography>
                          <MDDatePicker
                            defaultValue={today}
                            onChange={(val) => changeDate(val, "endDate")}
                            fullWidth
                          />
                        </MDBox>
                      </Grid>
                      <Grid item xs={6} md={2} lg={2}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDTypography variant="body2" fontWeight="regular" mb={1}>
                            Date Filter Type
                          </MDTypography>
                          <Controller
                            name="wonAt"
                            control={methods.control}
                            render={({ field, fieldState: { error: fieldError } }) => (
                              <Box display="flex" alignItems="center" gap={1}>
                                <MDTypography
                                  variant="body2"
                                  color={!field.value ? "info" : "text"}
                                >
                                  Created
                                </MDTypography>
                                <Switch
                                  {...field}
                                  checked={field.value || false}
                                  onChange={(event) => {
                                    field.onChange(event.target.checked);
                                    setWonAt(event.target.checked);
                                  }}
                                  size="small"
                                />
                                <MDTypography variant="body2" color={field.value ? "info" : "text"}>
                                  Won
                                </MDTypography>
                              </Box>
                            )}
                          />
                        </MDBox>
                      </Grid>
                      <Grid item xs={6} md={2} lg={2}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDTypography variant="body2" fontWeight="regular" mb={1}>
                            Company
                          </MDTypography>
                          <CompanyOptions
                            triggerRefetch={triggerRefetch}
                            selectedCompany={company}
                            setTriggerRefetch={(trigger) => setTriggerRefetch(trigger)}
                          >
                            {({ companyOptions, loading, search, pagination }) => (
                              <Controller
                                name="companyId"
                                control={methods.control}
                                render={({ field, fieldState: { error } }) => (
                                  <>
                                    <ScrollAsyncAutocomplete
                                      {...field}
                                      value={
                                        companyOptions?.find((x) => x.value === field.value) || null
                                      }
                                      options={companyOptions ?? []}
                                      loading={loading}
                                      label="Company"
                                      search={search}
                                      onLoadMore={pagination.loadMore}
                                      hasMore={pagination.hasNextPage}
                                      onChange={(value) => {
                                        field.onChange(value || null);
                                      }}
                                    />
                                  </>
                                )}
                              />
                            )}
                          </CompanyOptions>
                        </MDBox>
                      </Grid>

                      <Grid item xs={6} md={2} lg={2}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDTypography variant="body2" fontWeight="regular" mb={1}>
                            Estimator
                          </MDTypography>
                          <OrganizationUsersOptions>
                            {({ organizationUsersOptions, loading, search }) => (
                              <Controller
                                name="estimatorId"
                                control={methods.control}
                                render={({ field, fieldState: { error } }) => (
                                  <>
                                    <ScrollAsyncAutocomplete
                                      {...field}
                                      value={
                                        organizationUsersOptions?.find(
                                          (x) => x.value === field.value
                                        ) || null
                                      }
                                      options={organizationUsersOptions ?? []}
                                      loading={loading}
                                      label="Estimator"
                                      search={search}
                                      onLoadMore={() => {}}
                                      hasMore={false}
                                      onChange={(value) => {
                                        field.onChange(value || null);
                                        const newEstimatorId = value ? String(value) : "";
                                        setEstimatorId(newEstimatorId);
                                        // Clear estimator name when filter is cleared
                                        if (!newEstimatorId) {
                                          setEstimatorName("");
                                        }
                                      }}
                                    />
                                  </>
                                )}
                              />
                            )}
                          </OrganizationUsersOptions>
                        </MDBox>
                      </Grid>
                      {/* <Grid item xs={12} md={1.5} lg={1.5}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDTypography variant="body2" fontWeight="regular" mb={1}>
                            Job
                          </MDTypography>
                          <JobOptions>
                            {({ jobOptions, loading, search }) => (
                              <Controller
                                name="jobId"
                                control={methods.control}
                                render={({ field, fieldState: { error: fieldError } }) => (
                                  <ScrollAsyncAutocomplete
                                    {...field}
                                    value={jobOptions?.find((x) => x.value === field.value) || null}
                                    options={jobOptions ?? []}
                                    loading={loading}
                                    label="Job"
                                    search={search}
                                    onLoadMore={() => {}}
                                    hasMore={false}
                                    onChange={(value) => {
                                      field.onChange(value || null);
                                      const newJobId = value ? String(value) : "";
                                      setJobId(newJobId);
                                      // Set job name for display and clear when filter is cleared
                                      if (value) {
                                        const selectedOption = jobOptions?.find(
                                          (x) => x.value === value
                                        );
                                        setJobName(selectedOption?.label || "");
                                      } else {
                                        setJobName("");
                                      }
                                    }}
                                  />
                                )}
                              />
                            )}
                          </JobOptions>
                        </MDBox>
                      </Grid> */}
                      <Grid item xs={12} md={1.5} lg={1.5}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDTypography variant="body2" fontWeight="regular" mb={1}>
                            Product Type
                          </MDTypography>
                          <Controller
                            name="type"
                            control={methods.control}
                            render={({ field, fieldState: { error: fieldError } }) => (
                              <ScrollAsyncAutocomplete
                                {...field}
                                value={
                                  productTypeOptions?.find((x) => x.value === field.value) || null
                                }
                                options={productTypeOptions ?? []}
                                loading={false}
                                label="Product Type"
                                search={() => {}}
                                onLoadMore={() => {}}
                                hasMore={false}
                                onChange={(value) => {
                                  field.onChange(value || null);
                                  const newProductType = value ? String(value) : "";
                                  setProductType(newProductType);
                                  // Set product type name for display and clear when filter is cleared
                                  if (value) {
                                    const selectedOption = productTypeOptions?.find(
                                      (x) => x.value === value
                                    );
                                    setProductTypeName(selectedOption?.label || "");
                                  } else {
                                    setProductTypeName("");
                                  }
                                }}
                              />
                            )}
                          />
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={1.5} lg={1.5}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDTypography variant="body2" fontWeight="regular" mb={1}>
                            Status
                          </MDTypography>
                          <Controller
                            name="status"
                            control={methods.control}
                            render={({ field, fieldState: { error: fieldError } }) => (
                              <ScrollAsyncAutocomplete
                                {...field}
                                value={statusOptions?.find((x) => x.value === field.value) || null}
                                options={statusOptions ?? []}
                                loading={false}
                                label="Status"
                                search={() => {}}
                                onLoadMore={() => {}}
                                hasMore={false}
                                onChange={(value) => {
                                  field.onChange(value || null);
                                  const newStatus = value ? String(value) : "";
                                  setStatus(newStatus);
                                  // Set status name for display and clear when filter is cleared
                                  if (value) {
                                    const selectedOption = statusOptions?.find(
                                      (x) => x.value === value
                                    );
                                    setStatusName(selectedOption?.label || "");
                                  } else {
                                    setStatusName("");
                                  }
                                }}
                              />
                            )}
                          />
                        </MDBox>
                      </Grid>

                      <Grid item xs={6} md={1.5} lg={1.5}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <a ref={downloadFileRef} className="d-none" />
                          <MDButton color="success" type="submit" fullWidth sx={{ height: "40px" }}>
                            RUN REPORT
                          </MDButton>
                        </MDBox>
                      </Grid>
                      <Grid item xs={6} md={1} lg={1}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <a className="d-none" />
                          <MDButton
                            color="info"
                            onClick={handleDownload}
                            fullWidth
                            sx={{ height: "40px" }}
                          >
                            <Icon fontSize="small" sx={{ mr: 0.5 }}>
                              download
                            </Icon>
                          </MDButton>
                        </MDBox>
                      </Grid>
                      <Grid item xs={6} md={1} lg={1}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDButton
                            id="columns-visibility"
                            color="info"
                            onClick={handleMenuOpen}
                            fullWidth
                            sx={{ height: "40px" }}
                          >
                            <Icon fontSize="small" sx={{ mr: 0.5 }}>
                              visibility
                            </Icon>
                          </MDButton>
                          <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            PaperProps={{
                              style: {
                                maxHeight: 400,
                                minWidth: 250,
                              },
                            }}
                          >
                            {columns.map((column) => (
                              <MenuItem
                                key={column.accessor}
                                onClick={() => handleColumnVisibilityChange(column.accessor)}
                              >
                                <Checkbox
                                  checked={columnVisibility[column.accessor]}
                                  onChange={() => handleColumnVisibilityChange(column.accessor)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <ListItemText primary={column.Header} />
                              </MenuItem>
                            ))}
                          </Menu>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, mb: 2 }}>
              <Grid container alignItems="center">
                <Grid item xs>
                  <MDTypography variant="h6" color="text">
                    Report Results
                  </MDTypography>
                </Grid>
                <Grid item>
                  <MDTypography variant="body2" color="text">
                    {companyName ? `Company: ${companyName}, ` : ""}
                    {estimatorName ? `Estimator: ${estimatorName}, ` : ""}
                    {jobName ? `Job: ${jobName}, ` : ""}
                    {productTypeName ? `Product Type: ${productTypeName}, ` : ""}
                    {statusName ? `Status: ${statusName}, ` : ""}
                    {date.startDate && date.endDate ? (
                      <>
                        {wonAt ? "Won Period: " : "Created Period: "}
                        {formatDisplayDate(date.startDate)} - {formatDisplayDate(date.endDate)}
                      </>
                    ) : (
                      "Select date range"
                    )}
                  </MDTypography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 1.5 }} />
            </Box>

            <Grid container display="flex">
              <DataTable
                canSearch={false}
                table={tableData}
                showTotalEntries={true}
                isSorted={true}
                pagination={pagination}
                paginationProps={{ variant: "gradient", color: "info" }}
                entriesPerPageOptions={["25", "50"]}
              />
              {tableData?.modal}
            </Grid>
          </MDBox>
        </FormProvider>
      </DashboardPage>
    </Box>
  );
};

export default ReportProposalDetails;
